const API = process.env.REACT_APP_BACKEND_URL;

export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

/* iOS only allows web push inside a home-screen app (16.4+) */
export const needsInstallFirst = () => isIOS() && !isStandalone();

const urlBase64ToUint8Array = (value) => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
};

export const getPushStatus = async () => {
  const res = await fetch(`${API}/api/push/status`, { credentials: 'include' });
  if (res.status === 401) return null;           // not signed in. Nothing to offer
  if (!res.ok) throw new Error('status failed');
  return res.json();
};

export const enablePush = async () => {
  if (!pushSupported()) throw new Error('This browser cannot receive notifications.');
  if (needsInstallFirst()) throw new Error('Add Payday Relay to your home screen first.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? 'Notifications are blocked for this site. Turn them back on in your browser settings.'
      : 'Notifications were not enabled.');
  }

  const registration = await navigator.serviceWorker.ready;
  const keyRes = await fetch(`${API}/api/push/vapid-key`);
  const { public_key: publicKey, configured } = await keyRes.json();
  if (!configured || !publicKey) throw new Error('Notifications are not configured yet.');

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const res = await fetch(`${API}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    }),
  });
  if (!res.ok) throw new Error('Could not save your notification settings.');
  return res.json();
};

export const disablePush = async () => {
  let endpoint = null;
  if (pushSupported()) {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      endpoint = subscription.endpoint;
      await subscription.unsubscribe();
    }
  }
  await fetch(`${API}/api/push/unsubscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ endpoint }),
  });
};

export const sendTestPush = async () => {
  const res = await fetch(`${API}/api/push/test`, { method: 'POST', credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Could not send a test notification.');
  return data;
};
